<template>
	<BlockVisibilityObserver title="MAIN">
		<section class="kova-lp-hero">
			<!-- Lower slice of Ellipse 2974; the upper one is in LandingKovaHeader. -->
			<span aria-hidden="true" class="kova-lp-hero__glow" />

			<div class="kova-lp-hero__inner">
				<div class="kova-lp-hero__copy">
					<div class="kova-lp-hero__headline">
						<!-- Desktop only: on mobile the same block sits inside the header. -->
						<div class="kova-lp-hero__rating">
							<div class="kova-lp-hero__rating-score">
								<LandingKovaStars class="kova-lp-hero__stars" />
								<p class="kova-lp-hero__rating-value">{{ rating.score }}&nbsp; {{ rating.label }}</p>
							</div>
							<p class="kova-lp-hero__rating-caption">{{ rating.caption }}</p>
						</div>

						<h1 class="kova-lp-hero__title">{{ title }}</h1>
						<p v-if="subtitle" class="kova-lp-hero__subtitle">{{ subtitle }}</p>
					</div>

					<div class="kova-lp-hero__price">
						<p class="kova-lp-hero__price-label">{{ price.label }}</p>
						<div class="kova-lp-hero__price-row">
							<p class="kova-lp-hero__price-now">{{ price.now }}</p>
							<div class="kova-lp-hero__price-aside">
								<p class="kova-lp-hero__price-was">{{ price.was }}</p>
								<p class="kova-lp-hero__price-period">{{ price.period }}</p>
							</div>
						</div>
					</div>

					<p class="kova-lp-hero__description">{{ description }}</p>
				</div>

				<div class="kova-lp-hero__visual">
					<!-- Desktop counterpart of __glow: on mobile Ellipse 2974 lights the whole
					     panel, on desktop it lives inside the product card. -->
					<span aria-hidden="true" class="kova-lp-hero__card-glow" />

					<!-- Ground shadow: the un-cut product shot, alpha-masked down to the
					     ellipse under the vial, exactly the way Figma's "Mask group" builds
					     it (4841:24400). -->
					<span
						v-if="product.shadow"
						aria-hidden="true"
						class="kova-lp-hero__product-shadow"
						:style="[productStyle, { backgroundImage: `url(${product.shadow})`, maskImage: shadowMask }]"
					/>
					<picture class="kova-lp-hero__product-picture">
						<source v-if="product.imageDesktop" media="(min-width: 1024px)" :srcset="product.imageDesktop" />
						<img alt="" class="kova-lp-hero__product" :src="product.image" :style="productStyle" />
					</picture>

					<!-- Desktop only: on mobile the stamp rides the header instead of the card. -->
					<span aria-hidden="true" class="kova-lp-hero__stamp" />

					<!-- Desktop only: the mobile frame drops the stock badge. -->
					<p class="kova-lp-hero__badge">{{ product.badge }}</p>

					<ul class="kova-lp-hero__chips">
						<li v-for="feature in features" :key="feature" class="kova-lp-hero__chip">
							<InlineSvg class="kova-lp-hero__check" :src="checkIcon" />
							<span>{{ feature }}</span>
						</li>
					</ul>
				</div>

				<div class="kova-lp-hero__actions">
					<!-- Ellipse 2975 — dark vignette behind the CTA. -->
					<span aria-hidden="true" class="kova-lp-hero__vignette" />

					<div class="kova-lp-hero__promises">
						<p v-for="promise in promises" :key="promise.label" class="kova-lp-hero__promise">
							<span v-if="promise.icon === 'cross'" class="kova-lp-hero__promise-ring">
								<InlineSvg class="kova-lp-hero__cross" :src="crossIcon" />
							</span>
							<InlineSvg v-else class="kova-lp-hero__promise-check" :src="checkIcon" />
							<span>{{ promise.label }}</span>
						</p>
					</div>

					<button class="kova-lp-hero__cta" type="button" @click="onCtaClick">{{ ctaLabel }}</button>
				</div>
			</div>

			<!-- Desktop only: the mobile design has no counterpart for this strip yet. -->
			<div class="kova-lp-hero__marquee">
				<div class="kova-lp-hero__marquee-track">
					<div v-for="pass in 2" :key="pass" class="kova-lp-hero__marquee-run" :aria-hidden="pass === 2">
						<template v-for="item in trust" :key="item.label">
							<span class="kova-lp-hero__trust">
								<img alt="" class="kova-lp-hero__trust-icon" :src="trustIcons[item.icon]" />
								<span>{{ item.label }}</span>
							</span>
							<span aria-hidden="true" class="kova-lp-hero__trust-dot">·</span>
						</template>
					</div>
				</div>
			</div>
		</section>
	</BlockVisibilityObserver>
</template>

<script>
import BlockVisibilityObserver from '@/components/BlockVisibilityObserver.vue'
import LandingKovaStars from '@/modules/Landing/components/LandingKovaStars.vue'

import checkIcon from '@/assets/kova/landing/check.svg'
import crossIcon from '@/assets/kova/landing/cross.svg'
import trustClinicians from '@/assets/kova/landing/trust-clinicians.svg'
import trustEligibility from '@/assets/kova/landing/trust-eligibility.svg'
import trustHipaa from '@/assets/kova/landing/trust-hipaa.svg'
import trustLegitscript from '@/assets/kova/landing/trust-legitscript.svg'

const trustIcons = {
	clinicians: trustClinicians,
	eligibility: trustEligibility,
	hipaa: trustHipaa,
	legitscript: trustLegitscript,
}

export default {
	name: 'LandingKovaHero',

	components: { BlockVisibilityObserver, LandingKovaStars },

	props: {
		ctaLabel: {
			type: String,
			required: true,
		},

		description: {
			type: String,
			required: true,
		},

		// Feature chips over the product shot — 6 in both frames.
		features: {
			type: Array,
			required: true,
		},

		// { image, imageDesktop?, badge, width, tilt, shadow?, shadowMask? } — the
		// medication-specific part of the block. Each vial ships at its own size and angle.
		product: {
			type: Object,
			required: true,
		},

		// [{ icon: 'cross' | 'check', label }] — the pair above the CTA.
		promises: {
			type: Array,
			required: true,
		},

		// { label, now, was, period }
		price: {
			type: Object,
			required: true,
		},

		// { score, label, caption }
		rating: {
			type: Object,
			required: true,
		},

		// The DEV-35975 copy has no subtitle.
		subtitle: {
			type: String,
			default: '',
		},

		title: {
			type: String,
			required: true,
		},

		// [{ icon: keyof trustIcons, label }] — the certification marquee.
		trust: {
			type: Array,
			required: true,
		},
	},

	emits: ['progressLoaderCompleted'],

	setup() {
		return { checkIcon, crossIcon, trustIcons }
	},

	computed: {
		// Each vial is its own shot: different size, tilt and bleed off the right edge
		// of the 390px frame. Passed as custom properties rather than plain inline
		// styles so the desktop rules can still override them.
		productStyle() {
			return {
				'--kova-lp-vial-right': `${this.product.offsetRight}px`,
				'--kova-lp-vial-tilt': `${this.product.tilt}deg`,
				'--kova-lp-vial-top': `${this.product.offsetTop}px`,
				'--kova-lp-vial-width': `${this.product.width}px`,
			}
		},

		// The ground-shadow ellipse, in that shot's own coordinates (4841:24401).
		shadowMask() {
			return `radial-gradient(${this.product.shadowMask}, #000, transparent)`
		},
	},

	methods: {
		onCtaClick() {
			// Mapped to block MAIN by LANDING_CTA_BLOCK_NAMES in LandingView.
			this.$emit('progressLoaderCompleted', 'main_eligible')
		},
	},
}
</script>

<style lang="scss" scoped>
// Mobile (Figma 5026:7508): the hero continues the green panel the header starts,
// and closes it with the 32px bottom corners.
.kova-lp-hero {
	position: relative;
	overflow: hidden;

	padding-top: var(--size-20);

	font-family: var(--kova-lp-font);
	border-end-start-radius: var(--size-32);
	border-end-end-radius: var(--size-32);
	background: var(--kova-lp-hero-panel);
}

.kova-lp-hero__glow {
	position: absolute;
	left: 140px;
	top: -120px;
	width: 400px;
	height: 480px;

	border-radius: 50%;
	background: var(--kova-lp-hero-glow);
	filter: blur(80px);
}

.kova-lp-hero__inner {
	position: relative;
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
}

.kova-lp-hero__copy {
	box-sizing: border-box;
	display: flex;
	flex-direction: column;
	gap: var(--size-24);
	// The vial sits level with the price, below the headline (6172:42867), so the column
	// runs the full 16px-inset width; the description keeps clear of the vial itself.
	padding: var(--size-8) var(--size-16) 0;
}

.kova-lp-hero__headline {
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
}

.kova-lp-hero__rating {
	display: none; // desktop only — see the header for the mobile placement
}

.kova-lp-hero__title {
	margin: 0;

	font-size: var(--size-36);
	font-weight: 500;
	line-height: 1.12;
	letter-spacing: -0.72px;

	color: var(--kova-lp-hero-on-panel);
}

.kova-lp-hero__subtitle {
	max-width: 205px;
	margin: 0;

	font-size: var(--size-14);
	line-height: 1.5;

	color: var(--kova-lp-hero-on-panel);
}

.kova-lp-hero__price {
	display: flex;
	flex-direction: column;
	gap: var(--size-12);
}

.kova-lp-hero__price-label {
	margin: 0;

	font-size: var(--size-18);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.36px;

	color: var(--kova-lp-hero-accent);
}

.kova-lp-hero__price-row {
	display: flex;
	align-items: center;
	gap: var(--size-8);
}

.kova-lp-hero__price-now {
	margin: 0;

	font-size: var(--size-56);
	font-weight: 600;
	// Figma trims this box to the cap height (text-box-trim on 4843:27550), which
	// is 39px at 56px Poppins. Matching line-height reproduces it without relying
	// on `text-box`, which is still Chromium/WebKit-only.
	line-height: 39px;
	letter-spacing: -1.12px;

	color: var(--kova-lp-hero-accent);
}

.kova-lp-hero__price-aside {
	display: flex;
	flex-direction: column;
	gap: var(--size-4);
}

.kova-lp-hero__price-was {
	margin: 0;

	font-size: var(--size-20);
	font-weight: 500;
	line-height: var(--size-20);

	color: var(--kova-lp-hero-on-panel-soft);
	text-decoration: line-through;
}

.kova-lp-hero__price-period {
	margin: 0;

	font-size: var(--size-20);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.4px;

	color: var(--kova-lp-hero-accent);
}

.kova-lp-hero__description {
	// The vial runs alongside this paragraph, so it keeps to the frame's 234 (6172:42909) —
	// and gives way on phones narrower than 390, where the vial, anchored right, slides in.
	max-width: min(234px, calc(100% - 121px));
	margin: var(--size-8) 0 0;

	font-size: var(--size-16);
	line-height: var(--size-22);
	letter-spacing: -0.064px;

	color: var(--kova-lp-hero-on-panel);
}

.kova-lp-hero__visual {
	padding-inline: var(--size-16);
}

// The wrapper exists only to carry the desktop <source>; it must not add a box of
// its own, or the absolutely-positioned shot would gain an empty line beside it.
.kova-lp-hero__product-picture {
	display: contents;
}

.kova-lp-hero__product,
.kova-lp-hero__product-shadow {
	position: absolute;
	// Anchored right: the shot deliberately bleeds off the 390px frame, so tying it
	// to that edge keeps the crop right on any phone width. Offsets are against
	// __inner, which starts where Figma's copy section does; all four values come
	// from the medication (see productStyle) and the height follows the shot's ratio.
	right: var(--kova-lp-vial-right);
	top: var(--kova-lp-vial-top);
	width: var(--kova-lp-vial-width);
	height: auto;

	transform: rotate(var(--kova-lp-vial-tilt));
}

// Same box as the cut-out it sits under (productStyle), square like its photo.
.kova-lp-hero__product-shadow {
	aspect-ratio: 1;

	background-position: center;
	background-size: cover;
}

.kova-lp-hero__badge,
.kova-lp-hero__card-glow,
.kova-lp-hero__stamp {
	display: none; // desktop only
}

.kova-lp-hero__chips {
	display: flex;
	flex-direction: column;
	align-items: start;
	gap: var(--size-8);
	margin: 0;
	padding: 0;

	list-style: none;
}

.kova-lp-hero__chip {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	gap: var(--size-6);
	padding: var(--size-8);

	font-size: var(--size-14);
	font-weight: 500;
	line-height: 1.4;
	letter-spacing: -0.14px;

	border-radius: var(--size-12);
	color: var(--kova-lp-hero-chip-ink);
	background: var(--kova-lp-hero-chip-bg);
	// Inset shadow rather than a border: Figma strokes sit inside the box, so a
	// real border would add 2px to every chip and drift the list by ~10px.
	box-shadow: inset 0 0 0 1px var(--kova-lp-hero-chip-border);
	backdrop-filter: blur(100px);
}

// Figma stores icon-checkmark upside-down and icon-close as a plus, then flips /
// rotates them through wrapper transforms — mirrored here so the vectors stay
// untouched.
.kova-lp-hero__check {
	flex-shrink: 0;
	width: 11.81px;

	color: var(--kova-lp-hero-chip-ink);
	transform: scaleY(-1);
}

.kova-lp-hero__actions {
	position: relative;
	box-sizing: border-box;

	display: flex;
	flex-direction: column;
	align-items: center;
	gap: var(--size-16);
	margin-top: var(--size-8);
	padding: 0 var(--size-20) var(--size-24);
}

.kova-lp-hero__vignette {
	position: absolute;
	left: -145px;
	top: -20px;
	width: 664px;
	height: 209px;

	border-radius: 50%;
	background: var(--kova-lp-hero-shade);
	filter: blur(100px);
}

.kova-lp-hero__promises {
	position: relative;
	display: flex;
	justify-content: center;
	gap: var(--size-12);
}

.kova-lp-hero__promise {
	display: flex;
	align-items: center;
	gap: var(--size-4);
	margin: 0;

	font-size: var(--size-14);
	font-weight: 500;
	line-height: var(--size-20);
	letter-spacing: -0.14px;

	color: var(--kova-lp-hero-on-panel);
	white-space: nowrap;
}

.kova-lp-hero__promise-ring {
	box-sizing: border-box;
	display: flex;
	align-items: center;
	justify-content: center;
	overflow: hidden;
	padding: 1px;

	border: 1px solid var(--kova-lp-check);
	border-radius: var(--size-48);
}

.kova-lp-hero__cross {
	width: var(--size-12);
	height: var(--size-12);

	color: var(--kova-lp-check);
	transform: scaleY(-1) rotate(45deg);
}

.kova-lp-hero__promise-check {
	flex-shrink: 0;
	width: 13.5px;

	color: var(--kova-lp-check);
	transform: scaleY(-1);
}

// Figma sets this one from the DS Button (Inter); every other CTA on the landing
// is Poppins, so it follows suit rather than switching face on resize.
.kova-lp-hero__cta {
	position: relative;
	box-sizing: border-box;

	width: 100%;
	max-width: 350px;
	height: var(--size-48);
	padding: var(--size-12);

	font-family: inherit;
	font-size: var(--size-16);
	font-weight: 600;
	line-height: var(--size-18);

	border: none;
	border-radius: var(--size-16);
	color: var(--kova-lp-hero-cta-ink);
	background: var(--kova-lp-hero-accent);
	cursor: pointer;
}

.kova-lp-hero__marquee {
	display: none; // desktop only
}

// Desktop (Figma 5026:7483) is a different composition, not a wider mobile one:
// light page, two columns, and the green panel shrinks to the product card.
@media (min-width: 1024px) {
	.kova-lp-hero {
		padding-top: 0;

		border-radius: 0;
		background: var(--kova-lp-page);
	}

	.kova-lp-hero__glow {
		display: none;
	}

	.kova-lp-hero__inner {
		box-sizing: border-box;
		max-width: 1440px;
		margin-inline: auto;
		// The 796px hero frame leaves 62px under its 734px of content (5026:7481).
		padding: 0 var(--size-80) 62px;

		// 566 / 674 are the frame's two columns; as a ratio they hold their
		// proportion while the 80px gutters and the 40px gap stay put.
		display: grid;
		grid-template-columns: minmax(0, 566fr) minmax(0, 674fr);
		gap: var(--size-40);
		align-items: start;
	}

	.kova-lp-hero__copy {
		grid-column: 1;
		grid-row: 1;
		gap: var(--size-28);
		padding: 0;
	}

	.kova-lp-hero__headline {
		order: 1;
		gap: var(--size-20);
	}

	.kova-lp-hero__rating {
		align-self: start;

		display: flex;
		flex-direction: row;
		align-items: center;
		gap: var(--size-8);
		padding: var(--size-12) var(--size-16);

		border-radius: var(--size-16);
		background: var(--kova-lp-surface);
	}

	.kova-lp-hero__rating-score {
		display: flex;
		align-items: center;
		gap: var(--size-8);
	}

	.kova-lp-hero__stars {
		height: var(--size-16);
	}

	.kova-lp-hero__rating-value {
		margin: 0;

		font-size: var(--size-16);
		font-weight: 600;
		line-height: 1.4;

		color: var(--kova-lp-ink);
		white-space: nowrap;
	}

	.kova-lp-hero__rating-caption {
		margin: 0;

		font-size: var(--size-16);
		line-height: 1.4;

		color: var(--kova-lp-ink-70);
		white-space: nowrap;
	}

	// The DEV-35975 headline is far longer than the old one, so it takes the size
	// LandingKovaHeroPromo sets for the same copy (6172:49171) on every desktop width.
	.kova-lp-hero__title {
		font-size: var(--size-52);
		line-height: 58px; // 1.12 at 52px — no token between 56 and 60
		letter-spacing: -1.04px;

		color: var(--kova-lp-text);
	}

	.kova-lp-hero__subtitle {
		max-width: none;

		font-size: var(--size-24);
		font-weight: 600;
		line-height: 1.5;

		color: var(--kova-lp-brand);
	}

	// Under the price, in the accent green — as LandingKovaHeroPromo sets it and as the
	// DEV-35975 desktop frames draw it (6172:49180).
	.kova-lp-hero__description {
		order: 3;
		max-width: none;
		margin: 0;

		font-size: var(--size-24);
		font-weight: 500;
		line-height: var(--size-36);
		letter-spacing: normal;

		color: var(--kova-lp-brand);
	}

	// Figma hugs this row at 339px; the parts size themselves to their own text, so
	// a cap is enough and the row can narrow with the column.
	.kova-lp-hero__price {
		order: 2;
		max-width: 339px;
		gap: var(--size-20);
	}

	.kova-lp-hero__price-label,
	.kova-lp-hero__price-period {
		font-size: var(--size-24);
		line-height: 1.1;
		letter-spacing: -0.48px;

		color: var(--kova-lp-brand);
		// "per month" is one line in a 124px column (4841:21152) — a hair too tight
		// for Poppins to guarantee it on its own.
		white-space: nowrap;
	}

	.kova-lp-hero__price-row {
		align-items: end;
		gap: var(--size-16);
	}

	.kova-lp-hero__price-now {
		padding-block: var(--size-4);

		font-size: 92px; // 4841:21148 — between --size-88 and --size-96, kept exact
		line-height: var(--size-64); // cap height at 92px — see the mobile rule
		letter-spacing: -1.84px;

		color: var(--kova-lp-highlight);
	}

	.kova-lp-hero__price-aside {
		flex-shrink: 0;
		gap: var(--size-4);
	}

	.kova-lp-hero__price-was {
		font-size: var(--size-34);
		line-height: 1.4;

		color: var(--kova-lp-muted);
	}

	// section 1 (4841:20950) — the product card. Every child is placed in % of the
	// 674x720 frame so the card scales with the grid column.
	.kova-lp-hero__visual {
		position: relative;
		overflow: hidden;

		grid-column: 2;
		grid-row: 1 / span 2;
		aspect-ratio: 674 / 720;
		margin-top: var(--size-14);
		padding: 0;

		border-radius: var(--size-36);
		background: var(--kova-lp-hero-panel);
	}

	// Ellipse 2976 / 2975 — dark vignettes top and bottom, drawn under and over the
	// shot respectively, exactly as they are stacked in Figma.
	.kova-lp-hero__visual::before,
	.kova-lp-hero__visual::after {
		content: '';
		position: absolute;
		left: 0.742%;
		width: 98.516%;

		border-radius: 50%;
		background: var(--kova-lp-hero-shade);
		filter: blur(151px);
	}

	.kova-lp-hero__visual::before {
		top: -8.611%;
		height: 30%;
	}

	.kova-lp-hero__visual::after {
		top: 79.583%;
		height: 29.028%;
	}

	// Ellipse 2974 (4841:20952) — the light behind the vial.
	.kova-lp-hero__card-glow {
		position: absolute;
		left: 21.513%;
		top: 1.528%;
		width: 62.017%;
		height: 92.222%;

		display: block;
		border-radius: 50%;
		background: var(--kova-lp-hero-glow);
		filter: blur(151px);
	}

	// Figma stacks the bottom vignette under the badge and the chips; ::after is
	// painted last, so they are lifted instead.
	.kova-lp-hero__badge,
	.kova-lp-hero__chips {
		z-index: 1;
	}

	.kova-lp-hero__product,
	.kova-lp-hero__product-shadow {
		left: 16.389%;
		right: auto;
		top: 3.333%;
		width: 72.563%;
		height: auto;

		transform: none;
	}

	.kova-lp-hero__product-shadow {
		aspect-ratio: 1;
	}

	// free delivery (6172:49590) — the stamp moves from the header onto the card, exactly
	// as LandingKovaHeroPromo places it.
	.kova-lp-hero__stamp {
		position: absolute;
		right: 4.748%;
		top: 4.444%;
		width: 26.706%;
		aspect-ratio: 1;

		display: block;
		background: url('@/assets/kova/landing/promo-badge-free-delivery.png') center / contain no-repeat;
	}

	.kova-lp-hero__badge {
		position: absolute;
		left: 4.748%;
		top: 4.444%;

		display: flex;
		align-items: center;
		gap: var(--size-10);
		margin: 0;
		padding: var(--size-10) var(--size-20);

		font-size: var(--size-24);
		line-height: 1.5;

		border-radius: var(--size-100);
		color: var(--kova-lp-hero-badge);
		box-shadow: inset 0 0 0 1px var(--kova-lp-hero-badge); // inside stroke, as in Figma
		white-space: nowrap;
	}

	.kova-lp-hero__badge::before {
		content: '';
		width: var(--size-10);
		height: var(--size-10);

		border-radius: 50%;
		background: var(--kova-lp-hero-accent);
	}

	// Anchored to the bottom of the card, not the top: at 1440 that is the same
	// 74.721% offset Figma draws, but on a narrower card the extra chip rows then
	// grow upward into the empty middle instead of being clipped by `overflow`.
	.kova-lp-hero__chips {
		position: absolute;
		left: 4.748%;
		bottom: 4.28%;
		width: 89.169%;

		flex-direction: row;
		flex-wrap: wrap;
		gap: var(--size-6);
	}

	.kova-lp-hero__chip {
		gap: var(--size-6);
		padding: var(--size-12) var(--size-14);

		font-size: var(--size-16);
		letter-spacing: -0.16px;

		border-radius: var(--size-14);
	}

	.kova-lp-hero__check {
		width: 11.81px;
	}

	.kova-lp-hero__actions {
		grid-column: 1;
		grid-row: 2;
		align-items: start;
		gap: var(--size-32);
		margin-top: 0;
		padding: 0;
	}

	.kova-lp-hero__vignette {
		display: none;
	}

	.kova-lp-hero__promises {
		flex-wrap: wrap;
		gap: var(--size-16);
	}

	.kova-lp-hero__promise {
		gap: var(--size-8);

		font-size: var(--size-18);
		font-weight: 400;
		line-height: 1.44;
		letter-spacing: normal;

		color: var(--kova-lp-ink);
	}

	.kova-lp-hero__promise-ring {
		box-sizing: border-box;
		width: var(--size-24);
		height: var(--size-24);
		padding: var(--size-2);

		border-width: var(--size-2);
		border-radius: var(--size-80);
		border-color: var(--kova-lp-highlight);
	}

	.kova-lp-hero__cross {
		width: var(--size-18);
		height: var(--size-18);

		color: var(--kova-lp-highlight);
	}

	.kova-lp-hero__promise-check {
		width: 20.25px;

		color: var(--kova-lp-highlight);
	}

	// The second promise sits 4px from its icon while the first sits 8px
	// (4841:21155 vs 4841:21159).
	.kova-lp-hero__promise:last-child {
		gap: var(--size-4);
	}

	// Figma states 90px tall with 20/116 around a hugged label; a button centres its
	// own text, so the label just needs breathing room and a floor on the width.
	.kova-lp-hero__cta {
		width: auto;
		min-width: 368px;
		max-width: 100%;
		height: 90px; // 4841:21165 — between --size-88 and --size-96, kept exact
		padding: 0 var(--size-24);

		font-family: inherit;
		font-size: var(--size-24);
		font-weight: 500;
		line-height: normal;

		border-radius: var(--size-28);
		color: var(--Fill-White);
		background: var(--kova-lp-brand);
	}

	// lines (4841:21077) — certification ticker. The frame overflows its 1440px
	// board on the right, so it reads as a marquee; no timing was specified.
	.kova-lp-hero__marquee {
		position: relative;
		display: block;
		overflow: hidden;
		padding-block: var(--size-36);

		// The fade is a fifth of the board on each side (300 of 1440), so it stays a
		// percentage rather than a fixed distance.
		mask-image: linear-gradient(to right, transparent, #000 20%, #000 80%, transparent);
	}

	.kova-lp-hero__marquee-track {
		display: flex;
		width: max-content;

		animation: kova-lp-marquee 40s linear infinite;
	}

	.kova-lp-hero__marquee-run {
		display: flex;
		align-items: center;
		gap: var(--size-28);
		padding-left: var(--size-28);
	}

	.kova-lp-hero__trust {
		display: flex;
		align-items: center;
		gap: var(--size-6);
	}

	.kova-lp-hero__trust-icon {
		width: var(--size-42);
		height: var(--size-42);
	}

	.kova-lp-hero__trust,
	.kova-lp-hero__trust-dot {
		font-size: var(--size-34);
		font-weight: 500;
		line-height: 1.4;

		color: var(--kova-lp-muted);
		white-space: nowrap;
	}

	@keyframes kova-lp-marquee {
		to {
			transform: translateX(-50%);
		}
	}

	@media (prefers-reduced-motion: reduce) {
		.kova-lp-hero__marquee-track {
			animation: none;
		}
	}
}

// Compact desktop (1024-1279). Figma has no frame between mobile and 1440, and the
// display sizes it states for 1440 stop working once the columns have lost a third
// of their width — the chip list outgrows the product card. Only those sizes step
// down; the composition is unchanged.
@media (min-width: 1024px) and (max-width: 1279px) {
	// One line of "4.8 Excellent! Over 200,000+ happy customers" needs 427px; the
	// copy column is 376 here, so the pill is allowed to take a second row.
	.kova-lp-hero__rating {
		flex-wrap: wrap;
		row-gap: var(--size-4);
	}

	.kova-lp-hero__rating-value,
	.kova-lp-hero__rating-caption {
		font-size: var(--size-14);
		white-space: normal;
	}

	.kova-lp-hero__subtitle {
		font-size: var(--size-20);
	}

	.kova-lp-hero__price-label,
	.kova-lp-hero__price-period {
		font-size: var(--size-18);
	}

	.kova-lp-hero__price-now {
		font-size: var(--size-64);
		line-height: var(--size-44); // cap height at 64px — see the mobile rule
		letter-spacing: -1.28px;
	}

	.kova-lp-hero__price-was {
		font-size: var(--size-24);
	}

	.kova-lp-hero__badge {
		gap: var(--size-6);
		padding: var(--size-6) var(--size-12);

		font-size: var(--size-16);
	}

	.kova-lp-hero__badge::before {
		width: var(--size-6);
		height: var(--size-6);
	}

	.kova-lp-hero__chip {
		gap: var(--size-4);
		padding: var(--size-8) var(--size-10);

		font-size: var(--size-12);

		border-radius: var(--size-10);
	}

	.kova-lp-hero__check {
		width: var(--size-10);
	}

	.kova-lp-hero__promise {
		font-size: var(--size-16);
	}

	.kova-lp-hero__cta {
		min-width: 280px;
		height: var(--size-64);

		font-size: var(--size-18);

		border-radius: var(--size-20);
	}

	.kova-lp-hero__trust,
	.kova-lp-hero__trust-dot {
		font-size: var(--size-24);
	}

	.kova-lp-hero__trust-icon {
		width: var(--size-32);
		height: var(--size-32);
	}
}
</style>
